import { useEffect, useState } from 'react';
import { Save, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { ModalShell } from '../pages/admin/IncentiveDealClosures.jsx';

const SALES_DESIGNATIONS = [
    { value: '', label: '— not set —' },
    { value: 'bda', label: 'BDA' },
    { value: 'bde', label: 'BDE' },
    { value: 'bdm', label: 'BDM' },
    { value: 'bhead', label: 'BHead' },
    { value: 'operations', label: 'Operations' },
    { value: 'performance_marketing', label: 'Performance Marketing' },
    { value: 'other', label: 'Other (non-revenue)' },
];

const EMPTY = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'sales_user',
    designation: '',
    salesDesignation: '',
    officeBranch: '',
    territory: '',
    zohoUserId: '',
    reportsTo: '',
    status: 'active',
    skipOnboarding: true,
};

export default function CreateUserModal({ roles, onClose }) {
    const [f, setF] = useState(EMPTY);
    const [offices, setOffices] = useState([]);
    const [bdmCandidates, setBdmCandidates] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/auth/directory')
            .then(r => setOffices((r.data?.offices || []).map(o => (typeof o === 'string' ? o : (o.label || o.slug)))))
            .catch(() => {});
        api.get('/users/picker', { params: { salesDesignation: 'bdm' } })
            .then(r => setBdmCandidates(r.data.items || []))
            .catch(() => {});
    }, []);

    const set = (patch) => setF(prev => ({ ...prev, ...patch }));
    const isSalesRole = f.role === 'sales_user';

    const submit = async (e) => {
        e.preventDefault();
        if (!f.name.trim()) return toast.error('Name is required');
        if (!f.email.trim()) return toast.error('Email is required');
        if (f.password.length < 8) return toast.error('Password must be at least 8 characters');
        if (f.password !== f.confirmPassword) return toast.error('Passwords do not match');
        if (!f.role) return toast.error('Role is required');

        setBusy(true);
        try {
            const payload = {
                name: f.name.trim(),
                email: f.email.trim().toLowerCase(),
                password: f.password,
                phone: f.phone.trim() || undefined,
                role: f.role,
                designation: f.designation.trim() || undefined,
                salesDesignation: f.salesDesignation || undefined,
                officeBranch: f.officeBranch.trim() || undefined,
                territory: f.territory.trim() || undefined,
                zohoUserId: f.zohoUserId.trim() || undefined,
                reportsTo: f.reportsTo || undefined,
                status: f.status,
                skipOnboarding: f.skipOnboarding,
            };
            await api.post('/users', payload);
            toast.success(`${f.name} created`);
            onClose(true);
        } catch {
            /* api interceptor toasts */
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell title="Create user" onClose={() => onClose(false)} width="max-w-lg">
            <form className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" onSubmit={submit}>
                <p className="text-sm text-ink-500">
                    Provision a new account with role and profile fields. Active users can sign in immediately when onboarding is skipped.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <label className="ag-label">Full name *</label>
                        <input className="ag-input" value={f.name} onChange={e => set({ name: e.target.value })} required autoFocus />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="ag-label">Email *</label>
                        <input type="email" className="ag-input" value={f.email} onChange={e => set({ email: e.target.value })} required />
                    </div>
                    <div>
                        <label className="ag-label">Password *</label>
                        <input type="password" className="ag-input" value={f.password} onChange={e => set({ password: e.target.value })} minLength={8} required autoComplete="new-password" />
                    </div>
                    <div>
                        <label className="ag-label">Confirm password *</label>
                        <input type="password" className="ag-input" value={f.confirmPassword} onChange={e => set({ confirmPassword: e.target.value })} minLength={8} required autoComplete="new-password" />
                    </div>
                    <div>
                        <label className="ag-label">Phone</label>
                        <input type="tel" className="ag-input" value={f.phone} onChange={e => set({ phone: e.target.value })} />
                    </div>
                    <div>
                        <label className="ag-label">Role *</label>
                        <select className="ag-input" value={f.role} onChange={e => set({ role: e.target.value })} required>
                            {roles.map(r => (
                                <option key={r._id} value={r.slug}>{r.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="ag-label">Account status</label>
                        <select className="ag-input" value={f.status} onChange={e => set({ status: e.target.value })}>
                            <option value="active">Active — can sign in (when approved path applies)</option>
                            <option value="pending">Pending — must be approved later</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                            <input
                                type="checkbox"
                                className="rounded border-ink-300"
                                checked={f.skipOnboarding}
                                onChange={e => set({ skipOnboarding: e.target.checked })}
                                disabled={f.status !== 'active'}
                            />
                            Skip onboarding (KRA, training)
                        </label>
                    </div>
                </div>

                <div className="rounded-lg ring-1 ring-ink-200 bg-ink-50/30 p-3 space-y-3">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">Profile &amp; sales</div>
                    <div>
                        <label className="ag-label">Designation (display)</label>
                        <input className="ag-input" value={f.designation} onChange={e => set({ designation: e.target.value })} placeholder="e.g. Senior BDM, Noida" />
                    </div>
                    <div>
                        <label className="ag-label">Sales designation</label>
                        <select className="ag-input" value={f.salesDesignation} onChange={e => set({ salesDesignation: e.target.value })}>
                            {SALES_DESIGNATIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                        {isSalesRole && (
                            <div className="text-[11px] text-ink-500 mt-1">Required for client form pickers and incentives.</div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="ag-label">Office branch</label>
                            <input
                                className="ag-input"
                                list="create-user-offices"
                                value={f.officeBranch}
                                onChange={e => set({ officeBranch: e.target.value })}
                                placeholder="Noida"
                            />
                            <datalist id="create-user-offices">
                                {offices.map(b => <option key={b} value={b} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="ag-label">Territory</label>
                            <input className="ag-input" value={f.territory} onChange={e => set({ territory: e.target.value })} />
                        </div>
                    </div>
                    {['bda', 'bde'].includes(f.salesDesignation) && (
                        <div>
                            <label className="ag-label">Reports to (BDM)</label>
                            <select className="ag-input" value={f.reportsTo} onChange={e => set({ reportsTo: e.target.value })}>
                                <option value="">— default: BDM of office —</option>
                                {bdmCandidates.map(b => (
                                    <option key={b._id} value={b._id}>
                                        {b.name} · {b.officeBranch || 'no office'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="ag-label">Zoho user ID</label>
                        <input
                            className="ag-input font-mono text-xs"
                            value={f.zohoUserId}
                            onChange={e => set({ zohoUserId: e.target.value })}
                            placeholder="Optional — for My leads sync"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button type="submit" className="ag-btn-primary" disabled={busy}>
                        <UserPlus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create user'}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}
